import { Injectable } from '@angular/core';
import { Firestore, doc, docData, DocumentReference, Timestamp } from '@angular/fire/firestore';
import { BehaviorSubject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Traffic, TrafficPeriod } from '../_models/traffic';
import { ChartDataset } from 'chart.js';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class TrafficService {
  traffic = new BehaviorSubject<Traffic | null>(null);
  trafficSubscription?: Subscription;
  isLoaded = new BehaviorSubject(false);
  loadedSchoolId = '';
  trafficLineData = new BehaviorSubject<ChartDataset[]>(
    [
      { data: [], label: 'Results', type: 'line' },
      { data: [], label: 'Completion %', type: 'bar' },
    ]
  );
  trafficLabels = new BehaviorSubject<string[]>([]);
  isAllPeriods = new BehaviorSubject<boolean>(false);

  constructor(private firestore: Firestore) { }

  loadTraffic(schoolId: string) {
    if (!schoolId || schoolId === this.loadedSchoolId) {
      return;
    }

    this.isLoaded.next(false);
    this.traffic.next(null);
    if (this.trafficSubscription) {
      this.trafficSubscription.unsubscribe();
    }

    const docRef = doc(this.firestore, 'traffic', schoolId) as DocumentReference<Traffic>;
    this.trafficSubscription = docData(docRef).pipe(
      tap(res => {
        if (res) {
          console.log('Traffic loaded (or-reloaded)', schoolId);
          this.traffic.next(res);
          this.populateChartData(12);
        } else {
          console.log('No Traffic exists for', schoolId);
          this.traffic.next(null);
        }
        this.loadedSchoolId = schoolId;
        this.isLoaded.next(true);
      })
    ).subscribe();
  }

  downloadButtonPush() {
    const csvData = this.convertToCSV(this.traffic.value?.trafficData ?? []);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const filename = this.loadedSchoolId + '-traffic.csv';

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }

  convertToCSV(periods: TrafficPeriod[]): string {
    let str = 'Date, Results, Visitors, Completion %\r\n';
    periods.forEach(row => {
      const visitorTotal = this.getVisitorTotal(row);
      const completionPercentage = visitorTotal ? Math.round((row.results / visitorTotal) * 100) : 0;
      str += formatDate(row.date.toDate(), 'MM/dd/yyyy', 'en-US') + ',' + row.results + ',' + visitorTotal + ',' + completionPercentage + '\r\n';
    });
    return str;
  }

  populateChartData(periods?: number, startDate?: Timestamp, endDate?: Timestamp) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = this.traffic.value?.trafficData ?? [];

    this.isAllPeriods.next(periods === undefined && startDate === undefined && endDate === undefined);

    const startDateTimestamp = periods && data.length - periods > 0 ? data[data.length - periods].date : startDate || this.traffic.value?.earliestPeriod;
    const endDateTimestamp = endDate || this.traffic.value?.latestPeriod;
    if (!startDateTimestamp || !endDateTimestamp) {
      console.error('Start or end date of chart data is missing, cannot populate chart data');
      return;
    }

    const filtered = data.filter(d => d.date >= startDateTimestamp && d.date <= endDateTimestamp);

    const vWelcome = filtered.map(x => {
      const visitorTotal = this.getVisitorTotal(x);
      return visitorTotal ? Math.round((x.results / visitorTotal) * 100) : 0;
    });
    const vNetPrice = filtered.map(x => x.results);
    const vLabels = filtered.map(x => months[x.date.toDate().getMonth()] + '-' + x.date.toDate().getFullYear());

    this.trafficLineData.next([
      {
        label: 'Results', data: vNetPrice, yAxisID: 'Results', type: 'line',
        backgroundColor: 'rgba(0, 51, 102, 0.8)',
        borderColor: 'rgba(0,51,102,1)',
        pointBackgroundColor: 'rgba(0,51,102,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(0,51,102,1)',
        fill: true,
        tension: .5,
      },
      {
        label: 'Completion %', data: vWelcome, yAxisID: 'Completion', type: 'bar',
        backgroundColor: 'rgba(45, 55, 72, 0.7)',
        borderColor: 'rgba(45,55,72,1)',
        datalabels: { display: false },
      },
    ]);
    this.trafficLabels.next(vLabels);
  }

  private getVisitorTotal(period: TrafficPeriod): number {
    if (!period) {
      return 0;
    }
    const visitors = typeof period.visitors === 'number' ? period.visitors : 0;
    const starts = typeof period.starts === 'number' ? period.starts : 0;
    return Math.max(visitors, starts);
  }
}
